import { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router"
import { useLocation } from "react-router-dom"

import { RoutesPaths } from "../constants/routes.ts"
import { type PermissionProjectPermissions } from "../containers/SettingsPermissions/types/permissions.types.ts"

interface ProjectPageGuardProps {
  permissions: PermissionProjectPermissions
}

export const ProjectPageNavigation = ({
  permissions
}: ProjectPageGuardProps) => {
  const { id } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const route = RoutesPaths.project

  const pages = useMemo(() => {
    if (!id) return {}

    return {
      [route.view(id).milestones]:
        permissions?.milestones?.project_milestone_view,
      [route.view(id).finance]: permissions?.finances?.project_finance_view,
      [route.view(id).invoices]: permissions?.invoices?.project_invoice_view,
      [route.view(id).tasks]: permissions?.checklists?.project_checklist_view,
      [route.view(id).commissions]:
        permissions?.commissions?.project_commission_view,
      [route.view(id).albums]: permissions?.docs?.project_doc_view,
      [route.view(id).logs]: permissions?.features?.project_activity_log
    }
  }, [id, permissions, route])

  const isCurrentPageAllowed = useMemo(
    () =>
      Object.entries(pages).some(
        ([key, value]) => pathname.includes(key) && value
      ),
    [pages, pathname]
  )

  const firstAllowedPage = useMemo(
    () => Object.entries(pages).find(([, value]) => value)?.[0],
    [pages]
  )

  useEffect(() => {
    if (isCurrentPageAllowed) return
    if (!firstAllowedPage) return
    if (firstAllowedPage === pathname) return // already there, avoid loop

    navigate(firstAllowedPage, { replace: true })
  }, [isCurrentPageAllowed, firstAllowedPage, pathname, navigate])

  return null
}
